/**
 * Cards v2 API — typed wrappers over /api/cards/:cardId/* endpoints
 * (Milestone 2.4, spec §3.6 + §3.7).
 *
 * Mirrors apps/chaos-gateway/src/cards-v2.ts. Provides:
 *   - PATCH wrappers for assignee, role, instructions, workflow,
 *     approval_policy (M2.1 extension columns on chaos.cards).
 *   - Member CRUD against chaos.card_members.
 *
 * EXTENDS the existing kanbanApi surface — does not replace it. Use
 * this module specifically for the new M2 fields; legacy card fields
 * (title, columnId, position, autonomyLevel, dueDate) stay on kanbanApi.
 */
import type {
  AddCardMemberInput,
  AssigneeKind,
  CardMember,
  CardResponsible,
  SetCardResponsibleInput,
  MemberKind,
  SetCardApprovalPolicyInput,
  SetCardAssigneeInput,
  SetCardInstructionsInput,
  SetCardRoleInput,
  SetCardWorkflowInput,
} from '@chaos/types/cards-v2';

import { apiJson } from './client';

// ── Response wrappers ───────────────────────────────────────────────
interface CardPatchResponse<TBody> {
  card: TBody;
}
interface MembersListResponse {
  members: CardMember[];
}
interface MemberResponse {
  member: CardMember;
}

export interface CardAssigneePatchResult {
  id: string;
  assigneeKind: AssigneeKind | null;
  assigneeUserId: string | null;
  assigneeRoleId: string | null;
  updatedAt: string;
}
export interface CardRolePatchResult {
  id: string;
  roleId: string | null;
  updatedAt: string;
}
export interface CardInstructionsPatchResult {
  id: string;
  cardInstructions: string | null;
  updatedAt: string;
}
export interface CardWorkflowPatchResult {
  id: string;
  workflow: unknown;
  updatedAt: string;
}
export interface CardApprovalPolicyPatchResult {
  id: string;
  approvalPolicy: string | null;
  updatedAt: string;
}
export interface SetCardPermissionModeInput {
  permissionMode: 'derive' | 'default' | 'bypassPermissions' | 'acceptEdits' | 'plan' | null;
}
export interface CardPermissionModePatchResult {
  id: string;
  permissionMode: string | null;
  updatedAt: string;
}
/**
 * Card run-ceiling state (GET/PATCH /api/cards/:cardId/run-ceiling). The brake
 * caps consecutive agent runs admitted without a human turn. `override` is the
 * per-card cap (null → inherit the account default → hard fallback);
 * `effective` is the resolved cap that actually applies. `enforced` echoes the
 * CARD_CEILINGS_ENABLED gateway flag.
 */
export interface CardRunCeiling {
  cardId: string;
  enforced: boolean;
  hardDefault: number;
  override: number | null;
  accountDefault: number | null;
  effective: number;
  consecutive: number;
}
export interface SetCardRunCeilingInput {
  /** null clears the per-card override (fall back to the account default). */
  maxConsecutiveRuns: number | null;
}

export const cardsV2Api = {
  // ── The card's ONE responsible person (migration 0414) ─────────────
  getResponsible(cardId: string): Promise<CardResponsible> {
    return apiJson<CardResponsible>(`/cards/${cardId}/responsible`);
  },
  setResponsible(cardId: string, input: SetCardResponsibleInput): Promise<CardResponsible> {
    return apiJson<CardResponsible>(`/cards/${cardId}/responsible`, {
      method: 'PATCH',
      json: input,
    });
  },
  // ── PATCH wrappers ─────────────────────────────────────────────────
  setAssignee(cardId: string, input: SetCardAssigneeInput): Promise<CardAssigneePatchResult> {
    return apiJson<CardPatchResponse<CardAssigneePatchResult>>(
      `/cards/${cardId}/assignee`,
      { method: 'PATCH', json: input },
    ).then((r) => r.card);
  },
  setRole(cardId: string, input: SetCardRoleInput): Promise<CardRolePatchResult> {
    return apiJson<CardPatchResponse<CardRolePatchResult>>(`/cards/${cardId}/role`, {
      method: 'PATCH',
      json: input,
    }).then((r) => r.card);
  },
  setInstructions(
    cardId: string,
    input: SetCardInstructionsInput,
  ): Promise<CardInstructionsPatchResult> {
    return apiJson<CardPatchResponse<CardInstructionsPatchResult>>(
      `/cards/${cardId}/instructions`,
      { method: 'PATCH', json: input },
    ).then((r) => r.card);
  },
  setWorkflow(cardId: string, input: SetCardWorkflowInput): Promise<CardWorkflowPatchResult> {
    return apiJson<CardPatchResponse<CardWorkflowPatchResult>>(
      `/cards/${cardId}/workflow`,
      { method: 'PATCH', json: input },
    ).then((r) => r.card);
  },
  setApprovalPolicy(
    cardId: string,
    input: SetCardApprovalPolicyInput,
  ): Promise<CardApprovalPolicyPatchResult> {
    return apiJson<CardPatchResponse<CardApprovalPolicyPatchResult>>(
      `/cards/${cardId}/approval-policy`,
      { method: 'PATCH', json: input },
    ).then((r) => r.card);
  },
  setPermissionMode(
    cardId: string,
    input: SetCardPermissionModeInput,
  ): Promise<CardPermissionModePatchResult> {
    return apiJson<CardPatchResponse<CardPermissionModePatchResult>>(
      `/cards/${cardId}/permission-mode`,
      { method: 'PATCH', json: input },
    ).then((r) => r.card);
  },

  // ── Run-ceiling (card_max_consecutive_runs override) ───────────────
  getRunCeiling(cardId: string): Promise<CardRunCeiling> {
    return apiJson<CardRunCeiling>(`/cards/${cardId}/run-ceiling`);
  },
  setRunCeiling(cardId: string, input: SetCardRunCeilingInput): Promise<CardRunCeiling> {
    return apiJson<CardRunCeiling>(`/cards/${cardId}/run-ceiling`, {
      method: 'PATCH',
      json: input,
    });
  },

  // ── Member CRUD ────────────────────────────────────────────────────
  listMembers(cardId: string): Promise<CardMember[]> {
    return apiJson<MembersListResponse>(`/cards/${cardId}/members`).then((r) => r.members);
  },
  addMember(cardId: string, input: AddCardMemberInput): Promise<CardMember> {
    return apiJson<MemberResponse>(`/cards/${cardId}/members`, {
      method: 'POST',
      json: input,
    }).then((r) => r.member);
  },
  removeMember(
    cardId: string,
    memberKind: MemberKind,
    memberId: string,
  ): Promise<{ ok: boolean }> {
    return apiJson<{ ok: boolean }>(
      `/cards/${cardId}/members/${memberKind}/${memberId}`,
      { method: 'DELETE' },
    );
  },
};

export const cardsV2Keys = {
  all: ['cards-v2'] as const,
  members: (cardId: string) => [...cardsV2Keys.all, 'members', cardId] as const,
};
